import { RewriteToneKind, TranslateLangKind } from "client/Api/AiAssistant/aiAssistant";
import { useValueEqualMemo } from "client/Hooks";
import { htmlEscape } from "client/Misc/htmlUtils";
import { isNilOrWhiteSpace } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { useCallback, useMemo } from "react";
import {
	RewriteToolHandlers,
	RewriteToolResponse,
	RewriteToolsResult,
	SuccessfulRewriteToolResponse
} from "./common";

const MAX_CONTENT_LENGTH = 20_000;

export type RewriteToolsService = {
	makeShorter: (content: string, signal?: AbortSignal) => Promise<string>;
	makeLonger: (content: string, signal?: AbortSignal) => Promise<string>;
	fixSpelling: (content: string, signal?: AbortSignal) => Promise<string>;
	changeTone: (content: string, tone: RewriteToneKind, signal?: AbortSignal) => Promise<string>;
	translate: (content: string, lang: TranslateLangKind, signal?: AbortSignal) => Promise<string>;
};

export type PlainTextRewriteToolsRange =
	| {
		kind: "entire-content";
	}
	| {
		kind: "specific-range";
		from: number;
		to: number;
		shouldUseEntireContentWhenEmpty?: boolean;
	};

export function usePlainTextRewriteTools(
	value: string,
	onChange: (newValue: string) => void,
	onFocusInput: () => void,
	range: PlainTextRewriteToolsRange,
	service: RewriteToolsService
): RewriteToolsResult {
	const stableRange = useValueEqualMemo(
		(): PlainTextRewriteToolsRange => range,
		[range]
	);

	const [from, to] = useMemo(
		(): [number, number] => {
			const entireContentRange: [number, number] = [0, value.length];
			switch (stableRange.kind)
			{
				case "specific-range":
					let {
						from,
						to
					} = stableRange;

					if (from > to) {
						const temp: number = from;
						from = to;
						to = temp;
					}

					const isEmpty: boolean = from === to;

					if (isEmpty
						&& stableRange.shouldUseEntireContentWhenEmpty
					) {
						return entireContentRange;
					}

					return [from, to];
				case "entire-content":
					return entireContentRange;
				default:
					never(stableRange);
			}
		},
		[stableRange, value.length]
	);

	const applyHandler = useCallback(
		(response: SuccessfulRewriteToolResponse): void => {
			const {
				updatedSlice,
				originalSliceInfo
			} = response;

			const {
				entireContent: originalContent,
				from,
				to
			} = originalSliceInfo;

			if (value !== originalContent) {
				throw new Error("Content has changed");
			}

			if (from < 0
				|| to < 0
				|| from > value.length
				|| to > value.length
			) {
				throw new Error("The range is out of bounds");
			}

			const updatedValue: string = value.slice(0, from)
				+ updatedSlice
				+ value.slice(to, value.length);

			onChange(updatedValue);
			onFocusInput();
		},
		[
			onChange,
			onFocusInput,
			value
		]
	);

	return useMemo(
		(): RewriteToolsResult => {
			const slice: string = value.slice(from, to);

			if (isNilOrWhiteSpace(slice)) {
				return {
					canRewrite: false
				};
			}

			const handlers: RewriteToolHandlers = {
				makeShorter: (signal) => getRewriteToolResponse(
					value,
					from,
					to,
					(content, signal) => service.makeShorter(content, signal),
					signal
				),
				makeLonger: (signal) => getRewriteToolResponse(
					value,
					from,
					to,
					(content, signal) => service.makeLonger(content, signal),
					signal
				),
				fixSpelling: (signal) => getRewriteToolResponse(
					value,
					from,
					to,
					(content, signal) => service.fixSpelling(content, signal),
					signal
				),
				changeTone: (tone, signal) => getRewriteToolResponse(
					value,
					from,
					to,
					(content, signal) => service.changeTone(content, tone, signal),
					signal
				),
				translate: (lang, signal) => getRewriteToolResponse(
					value,
					from,
					to,
					(content, signal) => service.translate(content, lang, signal),
					signal
				),
				apply: applyHandler,
				getPreviewHtml: generatePreviewHtml,
				maxContentLength: MAX_CONTENT_LENGTH
			};

			return {
				canRewrite: true,
				handlers
			};
		},
		[
			value,
			applyHandler,
			from,
			to,
			service
		]
	);
}

async function getRewriteToolResponse(
	value: string,
	from: number,
	to: number,
	rewriteContentCallback: (originalContent: string, signal?: AbortSignal) => Promise<string>,
	signal: AbortSignal | undefined
): Promise<RewriteToolResponse> {
	const slice: string = value.slice(from, to);

	if (slice.length > MAX_CONTENT_LENGTH) {
		return {
			status: "content-too-large",
			originalSliceInfo: {
				entireContent: value,
				from,
				to
			}
		};
	}

	const updatedSlice: string = await rewriteContentCallback(slice, signal);

	return {
		status: "success",
		originalSliceInfo: {
			entireContent: value,
			from,
			to
		},
		updatedSlice
	};
}
function generatePreviewHtml(response: SuccessfulRewriteToolResponse): string {
	const {
		updatedSlice
	} = response;

	return `<p>${htmlEscape(updatedSlice)}</p>`;
}